import { createReducer, on } from '@ngrx/store';

import { InboxActions } from './inbox.actions';
import { InboxStateModel, initialInboxState, initialInboxViewState } from './inbox.model';

const withoutItem = (ids: string[], itemId: string): string[] => ids.filter(id => id !== itemId);

/**
 * Appends a page, keyed by id. State order does not matter — `visibleInboxItems` sorts the whole
 * union — so a duplicate that a concurrent write pushed across the page boundary is dropped rather
 * than reconciled into position.
 */
const appendPage = <T extends { id: string }>(existing: T[], page: T[]): T[] => {
  const seen = new Set(existing.map(entry => entry.id));
  return [...existing, ...page.filter(entry => !seen.has(entry.id))];
};

/** Order matters here, unlike `appendPage` — a thread renders in the order it is stored. */
const prependPage = <T extends { id: string }>(existing: T[], page: T[]): T[] => {
  const seen = new Set(existing.map(entry => entry.id));
  return [...page.filter(entry => !seen.has(entry.id)), ...existing];
};

export const inboxReducer = createReducer<InboxStateModel>(
  initialInboxState,

  // The rows outlive the page, the way it was being looked at does not.
  on(InboxActions.opened, (state): InboxStateModel => ({ ...state, ...initialInboxViewState })),
  on(InboxActions.filterChanged, (state, { filter }): InboxStateModel => ({ ...state, filter })),
  on(InboxActions.itemSelected, (state, { itemId }): InboxStateModel => ({ ...state, selectedItemId: itemId })),
  on(InboxActions.searchChanged, (state, { searchTerm }): InboxStateModel => ({ ...state, searchTerm })),
  on(
    InboxActions.sortToggled,
    (state): InboxStateModel => ({ ...state, sortDirection: state.sortDirection === 'newest' ? 'oldest' : 'newest' })
  ),

  on(
    InboxActions.loadAlerts,
    InboxActions.loadMoreAlerts,
    (state): InboxStateModel => ({ ...state, alertsLoading: true })
  ),
  on(
    InboxActions.loadAlertsSuccess,
    (state, { response }): InboxStateModel => ({
      ...state,
      alerts: response.alerts,
      alertsHasMore: response.hasMore,
      alertsLoading: false,
      alertsPage: 1,
      // The list response carries the authoritative unread count for its own half of the badge.
      counts: {
        ...state.counts,
        alerts: response.unreadCount,
        total: response.unreadCount + state.counts.conversations,
      },
    })
  ),
  on(InboxActions.loadAlertsFailure, (state): InboxStateModel => ({ ...state, alertsLoading: false })),

  // The unread count is left alone: it counts the whole stream, not the pages loaded so far.
  on(
    InboxActions.loadMoreAlertsSuccess,
    (state, { response }): InboxStateModel => ({
      ...state,
      alerts: appendPage(state.alerts, response.alerts),
      alertsHasMore: response.hasMore,
      alertsLoading: false,
      alertsPage: state.alertsPage + 1,
    })
  ),

  on(
    InboxActions.loadConversations,
    InboxActions.loadMoreConversations,
    (state): InboxStateModel => ({ ...state, conversationsLoading: true })
  ),
  on(
    InboxActions.loadConversationsSuccess,
    (state, { response }): InboxStateModel => ({
      ...state,
      conversations: response.conversations,
      conversationsHasMore: response.hasMore,
      conversationsLoading: false,
      conversationsPage: 1,
    })
  ),
  on(
    InboxActions.loadMoreConversationsSuccess,
    (state, { response }): InboxStateModel => ({
      ...state,
      conversations: appendPage(state.conversations, response.conversations),
      conversationsHasMore: response.hasMore,
      conversationsLoading: false,
      conversationsPage: state.conversationsPage + 1,
    })
  ),
  on(InboxActions.loadConversationsFailure, (state): InboxStateModel => ({ ...state, conversationsLoading: false })),

  on(InboxActions.loadCountsSuccess, (state, { counts }): InboxStateModel => ({ ...state, counts })),

  on(InboxActions.alertUpdated, (state, { alert }): InboxStateModel => {
    const alerts = state.alerts.map(existing => (existing.id === alert.id ? alert : existing));
    // The list only ever shows the active view, so resolving takes an alert out of it.
    const stillBelongs = !alert.resolvedAt;
    return {
      ...state,
      alerts: stillBelongs ? alerts : alerts.filter(existing => existing.id !== alert.id),
    };
  }),

  on(
    InboxActions.loadLinkedItem,
    (state, { itemId }): InboxStateModel => ({
      ...state,
      linkedLoadingItemIds: state.linkedLoadingItemIds.includes(itemId)
        ? state.linkedLoadingItemIds
        : [...state.linkedLoadingItemIds, itemId],
    })
  ),
  on(
    InboxActions.loadLinkedItemSuccess,
    (state, { itemId, linked }): InboxStateModel => ({
      ...state,
      linkedByItemId: { ...state.linkedByItemId, [itemId]: linked },
      linkedLoadingItemIds: withoutItem(state.linkedLoadingItemIds, itemId),
    })
  ),
  // Nothing is cached on failure: the card stays hidden, and reopening the row tries again.
  on(
    InboxActions.loadLinkedItemFailure,
    (state, { itemId }): InboxStateModel => ({
      ...state,
      linkedLoadingItemIds: withoutItem(state.linkedLoadingItemIds, itemId),
    })
  ),

  on(
    InboxActions.loadMessages,
    InboxActions.loadMoreMessages,
    (state): InboxStateModel => ({ ...state, messagesLoading: true })
  ),
  on(
    InboxActions.loadMessagesSuccess,
    (state, { conversationId, response }): InboxStateModel => ({
      ...state,
      messagesByConversation: { ...state.messagesByConversation, [conversationId]: response.messages },
      messagesPagingByConversation: {
        ...state.messagesPagingByConversation,
        [conversationId]: { page: 1, hasMore: response.hasMore },
      },
      messagesLoading: false,
    })
  ),
  // Prepended, not appended: page 2 is the next 50 older messages, so it belongs above the thread.
  on(
    InboxActions.loadMoreMessagesSuccess,
    (state, { conversationId, response }): InboxStateModel => ({
      ...state,
      messagesByConversation: {
        ...state.messagesByConversation,
        [conversationId]: prependPage(state.messagesByConversation[conversationId] ?? [], response.messages),
      },
      messagesPagingByConversation: {
        ...state.messagesPagingByConversation,
        [conversationId]: {
          page: (state.messagesPagingByConversation[conversationId]?.page ?? 1) + 1,
          hasMore: response.hasMore,
        },
      },
      messagesLoading: false,
    })
  ),
  on(InboxActions.loadMessagesFailure, (state): InboxStateModel => ({ ...state, messagesLoading: false })),

  on(InboxActions.sendMessage, (state): InboxStateModel => ({ ...state, sending: true })),
  on(InboxActions.sendMessageSuccess, (state, { message }): InboxStateModel => {
    const existing = state.messagesByConversation[message.conversationId] ?? [];
    // The 10-second poll can land the same message first; keyed by id so it isn't shown twice.
    const messages = existing.some(m => m.id === message.id) ? existing : [...existing, message];

    return {
      ...state,
      sending: false,
      messagesByConversation: { ...state.messagesByConversation, [message.conversationId]: messages },
      conversations: state.conversations.map(conversation =>
        conversation.id === message.conversationId
          ? {
              ...conversation,
              lastMessageAt: message.createdAt,
              lastMessagePreview: message.body,
              unread: false,
            }
          : conversation
      ),
    };
  }),
  on(InboxActions.sendMessageFailure, (state): InboxStateModel => ({ ...state, sending: false })),

  on(
    InboxActions.conversationRead,
    (state, { conversationId }): InboxStateModel => ({
      ...state,
      conversations: state.conversations.map(conversation =>
        conversation.id === conversationId ? { ...conversation, unread: false } : conversation
      ),
    })
  ),

  on(
    InboxActions.createConversationSuccess,
    (state, { conversation }): InboxStateModel => ({
      ...state,
      // Idempotent server-side, so a repeat compose returns the existing thread rather than a second one.
      conversations: state.conversations.some(existing => existing.id === conversation.id)
        ? state.conversations
        : [conversation, ...state.conversations],
    })
  ),

  on(InboxActions.cleared, (): InboxStateModel => initialInboxState)
);
